import * as z from "zod";

export interface ParsedBisRow {
  email: string;
  productId: string;
  variantId: string;
  createdAt: Date;
}

export type ParseBisRowResult =
  | { success: true; data: ParsedBisRow }
  | { success: false; error: string };

export const BIS_CSV_COLUMNS = {
  email: ["Email", "email", "EMAIL"],
  productId: ["Product ID", "ProductID", "product_id", "productId"],
  variantId: ["Variant ID", "VariantID", "variant_id", "variantId"],
  created: ["Created", "created", "CreatedAt", "created_at", "createdAt"],
} as const;

const emailSchema = z.string().email();

export function findBisCsvColumnValue(
  row: Record<string, string>,
  possibleNames: readonly string[],
): string | undefined {
  for (const name of possibleNames) {
    if (row[name] !== undefined && row[name] !== "") {
      return row[name].trim();
    }
  }
  return undefined;
}

export function parseBisDate(dateString: string): Date | null {
  if (!dateString) {
    return null;
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return null;
  }
  return date;
}

export function normalizeBisEmail(email: string): string {
  return email.toLowerCase().trim();
}

export function isValidBisEmail(email: string): boolean {
  return emailSchema.safeParse(email).success;
}

export function parseBisCsvRow(
  row: Record<string, string>,
  rowNum: number,
): ParseBisRowResult {
  // Skip empty rows
  const rowValues = Object.values(row);
  if (rowValues.every((value) => !value?.toString().trim())) {
    return { success: false, error: `Row ${rowNum}: Empty row` };
  }

  const email = findBisCsvColumnValue(row, BIS_CSV_COLUMNS.email);
  const productId = findBisCsvColumnValue(row, BIS_CSV_COLUMNS.productId);
  const variantId = findBisCsvColumnValue(row, BIS_CSV_COLUMNS.variantId);
  const createdString = findBisCsvColumnValue(row, BIS_CSV_COLUMNS.created);

  // Validate required fields
  if (!email) {
    return { success: false, error: `Row ${rowNum}: Missing email` };
  }

  if (!isValidBisEmail(email)) {
    return { success: false, error: `Row ${rowNum}: Invalid email format` };
  }

  if (!productId) {
    return { success: false, error: `Row ${rowNum}: Missing Product ID` };
  }

  if (!variantId) {
    return { success: false, error: `Row ${rowNum}: Missing Variant ID` };
  }

  // Parse and validate date
  const createdAt = createdString ? parseBisDate(createdString) : new Date();
  if (createdString && !createdAt) {
    return {
      success: false,
      error: `Row ${rowNum}: Invalid date format: ${createdString}`,
    };
  }

  return {
    success: true,
    data: {
      email: normalizeBisEmail(email),
      productId,
      variantId,
      createdAt: createdAt!,
    },
  };
}

export function validateBisCsvColumns(
  columnKeys: string[],
): { valid: true } | { valid: false; errors: string[] } {
  const errors: string[] = [];

  const hasEmailColumn = BIS_CSV_COLUMNS.email.some((name) =>
    columnKeys.includes(name),
  );
  const hasProductIdColumn = BIS_CSV_COLUMNS.productId.some((name) =>
    columnKeys.includes(name),
  );
  const hasVariantIdColumn = BIS_CSV_COLUMNS.variantId.some((name) =>
    columnKeys.includes(name),
  );

  if (!hasEmailColumn) {
    errors.push(
      `Missing required column: Email. Expected one of: ${BIS_CSV_COLUMNS.email.join(", ")}`,
    );
  }
  if (!hasProductIdColumn) {
    errors.push(
      `Missing required column: Product ID. Expected one of: ${BIS_CSV_COLUMNS.productId.join(", ")}`,
    );
  }
  if (!hasVariantIdColumn) {
    errors.push(
      `Missing required column: Variant ID. Expected one of: ${BIS_CSV_COLUMNS.variantId.join(", ")}`,
    );
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  return { valid: true };
}
